import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import '../../../styles/aida-base.css';
import '../../../styles/aida-responsive.css';

/**
 * InstantPlayPillars Component
 * Three-pillar zigzag layout explaining how Playcast works
 * Pillar 1 (left text, right media): All Games on Any Platform
 * Pillar 2 (left media, right text): Hosts Power Sessions
 * Pillar 3 (left text, right media): Click and Play
 * Desktop: Staggered layout with decorative background overlays
 * Mobile: Stubbed for future implementation
 */

const WHITE = 'rgba(255, 255, 255, 1)';
const YELLOW = 'rgba(251, 191, 36, 1)';

const pillarTitleStyle: React.CSSProperties = {
  lineHeight: '25px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 900,
  fontSize: '25px',
  fontStyle: 'italic',
  color: WHITE,
  margin: 0,
  fontVariationSettings: "'wght' 900",
};

const pillarBodyStyle: React.CSSProperties = {
  lineHeight: '13px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '10px',
  color: WHITE,
  margin: 0,
};

const mobilePillarBodyStyle: React.CSSProperties = {
  lineHeight: '22px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '16px',
  color: WHITE,
  margin: '0 auto',
  maxWidth: '330px',
};

const mobilePillarTitleStyle: React.CSSProperties = {
  lineHeight: '38px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 900,
  fontSize: '34px',
  fontStyle: 'italic',
  color: WHITE,
  margin: 0,
  fontVariationSettings: "'wght' 900",
};

export const InstantPlayPillars = () => {
  const ref = useRef(null);
  const mobileRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const isMobileInView = useInView(mobileRef, { once: true, amount: 0.1 });

  return (
    <>
      {/* Desktop Layout */}
      <motion.section
        ref={ref}
        className="relative mx-auto hidden w-full lg:block"
        style={{
          width: '100%',
          height: '540px',
          backgroundColor: 'transparent',
          overflow: 'hidden',
          position: 'relative',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          style={{ position: 'relative', width: '100%', height: '100%' }}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          {/* Background Decorative Image - Right */}
          <img
            src="https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/4610144/image-0bdd0551-e329-4b7b-baf5-bbc90abfb745.webp"
            alt=""
            style={{
              position: 'absolute',
              left: '687.5px',
              top: '114.5px',
              width: '606px',
              height: '350px',
              transform: 'rotate(-270deg) scaleX(-1)',
              opacity: 0.43,
              zIndex: 1,
            }}
          />

          {/* Background Decorative Image - Left */}
          <img
            src="https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/4610144/image-0bdd0551-e329-4b7b-baf5-bbc90abfb745.webp"
            alt=""
            style={{
              position: 'absolute',
              left: '-227.88px',
              top: '147.9px',
              width: '590px',
              height: '338px',
              transform: 'rotate(89deg)',
              opacity: 0.27,
              zIndex: 1,
            }}
          />

          {/* ── Pillar 1: ALL GAMES ON ANY PLATFORM ── */}

          {/* Media - Right */}
          <div
            style={{
              position: 'absolute',
              left: '494px',
              top: '0px',
              width: '304px',
              height: '171px',
              borderRadius: '20px',
              overflow: 'hidden',
              zIndex: 5,
            }}
          >
            <img
              src="https://i.imgur.com/RJUgeti.gif"
              alt="Game demo"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '20px',
              }}
            />
          </div>

          {/* Title - Left */}
          <div
            style={{
              position: 'absolute',
              left: '240px',
              top: '47px',
              width: '250px',
              zIndex: 5,
            }}
          >
            <h1 style={pillarTitleStyle}>
              ALL <span style={{ color: YELLOW }}>GAMES</span> ON
            </h1>
            <h1 style={pillarTitleStyle}>ANY PLATFORM</h1>
          </div>

          {/* Body - Left */}
          <div
            style={{
              position: 'absolute',
              left: '240px',
              top: '107px',
              width: '250px',
              zIndex: 5,
            }}
          >
            <p style={pillarBodyStyle}>
              A single playable link gives users instant access to try the game
              right away, without needing to download or install anything.
            </p>
          </div>

          {/* ── Pillar 2: HOSTS POWER SESSIONS ── */}

          {/* Media - Left */}
          <div
            style={{
              position: 'absolute',
              left: '229px',
              top: '171px',
              width: '237px',
              height: '187px',
              borderRadius: '20px',
              overflow: 'hidden',
              zIndex: 5,
            }}
          >
            <img
              src="https://i.imgur.com/EozeXjZ.jpg"
              alt="Gameplay session"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '20px',
              }}
            />
          </div>

          {/* Title - Right */}
          <div
            style={{
              position: 'absolute',
              left: '505px',
              top: '240px',
              width: '294px',
              zIndex: 5,
            }}
          >
            <h1 style={pillarTitleStyle}>
              HOSTS POWER <span style={{ color: YELLOW }}>SESSIONS</span>
            </h1>
          </div>

          {/* Body - Right */}
          <div
            style={{
              position: 'absolute',
              left: '505px',
              top: '298px',
              width: '282px',
              zIndex: 5,
            }}
          >
            <p style={pillarBodyStyle}>
              A real PC from our Host network streams the game through Playcast,
              ensuring smooth, high-quality gameplay that runs instantly on any
              device.
            </p>
          </div>

          {/* ── Pillar 3: CLICK AND PLAY ── */}

          {/* Media - Right */}
          <div
            style={{
              position: 'absolute',
              left: '538px',
              top: '384px',
              width: '277px',
              height: '139px',
              borderRadius: '20px',
              overflow: 'hidden',
              zIndex: 5,
            }}
          >
            <img
              src="https://i.imgur.com/ILPDNy9.gif"
              alt="Click and play"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '20px',
              }}
            />
          </div>

          {/* Title - Left */}
          <div
            style={{
              position: 'absolute',
              left: '241px',
              top: '432px',
              width: '250px',
              zIndex: 5,
            }}
          >
            <h1 style={pillarTitleStyle}>
              CLICK AND <span style={{ color: YELLOW }}>PLAY</span>
            </h1>
          </div>

          {/* Body - Left */}
          <div
            style={{
              position: 'absolute',
              left: '241px',
              top: '469px',
              width: '249px',
              zIndex: 5,
            }}
          >
            <p style={pillarBodyStyle}>
              No installs. No waiting. Just gameplay. Every session is seamless,
              allowing you to experience the full game immediately—no downloads,
              updates, or barriers.
            </p>
          </div>
        </motion.div>
      </motion.section>

      {/* Mobile Layout - Stacked: image on top, text below, repeated */}
      <motion.section
        ref={mobileRef}
        className="relative flex w-full flex-col items-center lg:hidden"
        style={{
          width: '100%',
          backgroundColor: 'transparent',
          overflow: 'hidden',
          padding: '16px 20px 40px',
          gap: '48px',
        }}
        initial={{ opacity: 0 }}
        animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Pillar 1: ALL GAMES ON ANY PLATFORM */}
        <motion.div
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}
          initial={{ opacity: 0, y: 20 }}
          animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4, delay: 0, ease: 'easeOut' }}
        >
          <div style={{ width: '100%', height: '323px', borderRadius: '20px', overflow: 'hidden' }}>
            <img
              src="https://i.imgur.com/RJUgeti.gif"
              alt="Game demo"
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '20px', display: 'block' }}
            />
          </div>
          <div style={{ width: '100%', textAlign: 'center', marginTop: '16px' }}>
            <h1 style={{ ...mobilePillarTitleStyle, textAlign: 'center' }}>
              ALL <span style={{ color: YELLOW }}>GAMES </span>ON
            </h1>
            <h1 style={{ ...mobilePillarTitleStyle, textAlign: 'center' }}>
              ANY PLATFORM
            </h1>
          </div>
          <p style={{ ...mobilePillarBodyStyle, textAlign: 'center', width: '100%', marginTop: '9px' }}>
            A single playable link gives users instant access to try the game
            right away, without needing to download or install anything.
          </p>
        </motion.div>

        {/* Pillar 2: HOSTS POWER SESSIONS */}
        <motion.div
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}
          initial={{ opacity: 0, y: 20 }}
          animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4, delay: 0.1, ease: 'easeOut' }}
        >
          <div style={{ width: '100%', height: '257px', borderRadius: '20px', overflow: 'hidden' }}>
            <img
              src="https://i.imgur.com/EozeXjZ.jpg"
              alt="Gameplay session"
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '20px', display: 'block' }}
            />
          </div>
          <div style={{ width: '100%', textAlign: 'center', marginTop: '16px' }}>
            <h1 style={{ ...mobilePillarTitleStyle, textAlign: 'center' }}>
              HOSTS POWER <span style={{ color: YELLOW }}>SESSIONS</span>
            </h1>
          </div>
          <p style={{ ...mobilePillarBodyStyle, textAlign: 'center', width: '100%', marginTop: '9px' }}>
            A real PC from our Host network streams the game through Playcast,
            ensuring smooth, high-quality gameplay that runs instantly on any
            device.
          </p>
        </motion.div>

        {/* Pillar 3: CLICK AND PLAY */}
        <motion.div
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}
          initial={{ opacity: 0, y: 20 }}
          animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4, delay: 0.2, ease: 'easeOut' }}
        >
          <div style={{ width: '100%', height: '200px', borderRadius: '20px', overflow: 'hidden' }}>
            <img
              src="https://i.imgur.com/ILPDNy9.gif"
              alt="Click and play"
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '20px', display: 'block' }}
            />
          </div>
          <div style={{ width: '100%', textAlign: 'center', marginTop: '16px' }}>
            <h1 style={{ ...mobilePillarTitleStyle, textAlign: 'center' }}>
              CLICK AND <span style={{ color: YELLOW }}>PLAY</span>
            </h1>
          </div>
          <p style={{ ...mobilePillarBodyStyle, textAlign: 'center', width: '100%', marginTop: '9px' }}>
            No installs. No waiting. Just gameplay. Every session is seamless,
            allowing you to experience the full game immediately—no downloads,
            updates, or barriers.
          </p>
        </motion.div>
      </motion.section>
    </>
  );
};
